/**
 * FloatingWhatsApp Component
 * Floating WhatsApp button for quick support access
 * Appears on all pages in the bottom-right corner
 */
import React, { useState } from 'react';
import { FaWhatsapp, FaTimes } from 'react-icons/fa';

const FloatingWhatsApp = ({ 
  phoneLink = "https://wa.link/c15uzz",
  message = "Need help? Chat with us!",
  showTooltip = true 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showBubble, setShowBubble] = useState(true);

  // Auto-hide bubble after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowBubble(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  if (isDismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end space-y-2">
      {/* Chat Bubble Tooltip */}
      {showTooltip && showBubble && (
        <div 
          className="relative bg-white rounded-lg shadow-lg p-3 pr-8 max-w-[200px] sm:max-w-[240px] animate-bounce-slow"
          style={{ animation: 'fadeInUp 0.3s ease-out' }}
        >
          <button
            onClick={() => setShowBubble(false)}
            className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 p-1"
            aria-label="Dismiss"
          >
            <FaTimes size={12} />
          </button>
          <p className="text-gray-800 text-xs sm:text-sm font-medium">{message}</p>
          <div className="absolute -bottom-2 right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
        </div>
      )}

      {/* Hover Tooltip */}
      {isHovered && !showBubble && (
        <div className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
          Chat on WhatsApp
        </div>
      )}

      {/* WhatsApp Button */}
      <a
        href={phoneLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`
          flex items-center justify-center
          w-14 h-14 sm:w-16 sm:h-16
          bg-green-500 hover:bg-green-600
          rounded-full shadow-lg hover:shadow-xl
          transition-all duration-300 ease-in-out
          ${isHovered ? 'scale-110' : 'scale-100'}
          group
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid="floating-whatsapp-btn"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp 
          size={28} 
          className="text-white sm:w-8 sm:h-8 group-hover:animate-wiggle" 
        />
        
        {/* Pulse Animation Ring */}
        <span className="absolute w-14 h-14 sm:w-16 sm:h-16 bg-green-400 rounded-full animate-ping opacity-30"></span>
      </a>

      {/* Custom Styles */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        
        .group-hover\\:animate-wiggle:hover {
          animation: wiggle 0.5s ease-in-out;
        }
        
        .animate-bounce-slow {
          animation: bounce 2s infinite;
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
      `}</style>
    </div>
  );
};

export default FloatingWhatsApp;
